import { useState, useRef, useEffect, useMemo } from "react";
import { undertone_data } from "../../utils/piIduData";
import { clientPath } from "../../config/key";
import { getOpacityValue } from "../../utils/function";
import { Button } from "react-bootstrap";

const UndertoneComponent = ({
  pricestepData,
  setPricestepData,
  getValueByField,
}) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const undertoneRefs = useRef([]);
  const hasInitialFocus = useRef(false);
  const containerRef = useRef(null);
  
  // Memoize undertoneOptions to prevent unnecessary recalculations
  const undertoneOptions = useMemo(() => {
    return undertone_data[
      pricestepData?.saturation === "Fancy Deep"
        ? `${pricestepData?.color} Fancy Deep`
        : pricestepData?.color
    ] || [];
  }, [pricestepData?.color, pricestepData?.saturation]);

  // Set initial focused index based on selection (keeping 4+ logic unchanged)
  useEffect(() => {
    const activeIndex = pricestepData?.colorUndertone === "4+" 
      ? 4 
      : undertoneOptions.findIndex(item => item.value === pricestepData?.colorUndertone);
    
    if (activeIndex >= 0 && activeIndex < undertoneOptions.length) {
      setFocusedIndex(activeIndex);
    } else {
      setFocusedIndex(0);
    }
  }, [pricestepData?.colorUndertone, undertoneOptions]);

  // Focus the appropriate element when focusedIndex changes
  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < undertoneOptions.length) {
      undertoneRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex, undertoneOptions]);

  // Handle initial focus when component receives focus via tab
  useEffect(() => {
    const handleFocusIn = (e) => {
      if (containerRef.current?.contains(e.target) && !hasInitialFocus.current) {
        hasInitialFocus.current = true;
        
        // Keep the exact same 4+ logic as before
        const selectedIndex = pricestepData?.colorUndertone === "4+" 
          ? 4 
          : undertoneOptions.findIndex(item => item.value === pricestepData?.colorUndertone);
        
        if (selectedIndex >= 0 && selectedIndex < undertoneOptions.length) {
          setFocusedIndex(selectedIndex);
        } else {
          setFocusedIndex(0);
        }
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [pricestepData?.colorUndertone, undertoneOptions]);

  const handleKeyDown = (e, index) => {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        setFocusedIndex(prev => {
          const nextIndex = Math.min(prev + 1, undertoneOptions.length - 1);
          return nextIndex;
        });
        break;
      case 'ArrowLeft':
        e.preventDefault();
        setFocusedIndex(prev => {
          const prevIndex = Math.max(prev - 1, 0);
          return prevIndex;
        });
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        selectUndertone(index);
        setTimeout(() => {
          const nextField = document.querySelector(`[data-field="calculate-price"]`);
          if (nextField) {
            nextField.focus();
          }
        }, 50);
        break;
      case 'Tab':
        if (!e.shiftKey && index === undertoneOptions.length - 1) {
          e.preventDefault();
          const nextField = document.querySelector(`[data-field="calculate-price"]`);
          if (nextField) nextField.focus();
        } else if (e.shiftKey && index === 0) {
          setFocusedIndex(-1);
        }
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(undertoneOptions.length - 1);
        break;
      default:
        break;
    }
  };

  const selectUndertone = (index) => {
    // Keep the exact same 4+ logic as before
    const selectedValue = (index === 4 && pricestepData?.color !== "Yellow") ?  "4+" : undertoneOptions[index]?.value;
    setPricestepData({
      ...pricestepData,
      colorUndertone: selectedValue,
    });
  };

  return (
    <div 
      className="idu-section-container" 
      role="group" 
      aria-labelledby="undertone-label"
      ref={containerRef}
      tabIndex="-1"
    >
      <div className="idu-top-section">
        <h5 id="undertone-label">undertone</h5>
        <p style={{
          lineHeight: pricestepData?.color === "Yellow" && pricestepData?.saturation !== "Fancy Deep"
            ? "19px"
            : "22px",
        }}>
          {pricestepData?.color === "Yellow" && pricestepData?.saturation !== "Fancy Deep"
            ? `Measures the deviation from the pure yellow hue, ranging from a strong green sensation to a brown influence.`
            : `The amount of Gray sensation influencing ${pricestepData?.color?.toLowerCase()} diamonds.`}{" "}
          <span
            onClick={() => getValueByField("color Undertone")}
            // tabIndex="0"
            data-field="color-undertone-info"
            onKeyDown={(e) => e.key === 'Enter' && getValueByField("color Undertone")}
          >
            Read more…
          </span>
        </p>
      </div>
      <div className="idu-main-section">
        <div className="idu-undertone-arrow-section">
          {pricestepData?.color === "Yellow" && pricestepData?.saturation !== "Fancy Deep" && (
            <>
              <div className="undertone-arrow">
                <p>WARM</p>
                <img src={`${clientPath}/icons/undertone-arrow-left.png`} alt="" />
              </div>
              <div className="undertone-arrow-text">
                <p>primary yellow</p>
              </div>
              <div className="undertone-arrow">
                <p>COLD</p>
                <img src={`${clientPath}/icons/undertone-arrow-right.png`} alt="" />
              </div>
            </>
          )}
        </div>

        <div className="idu-image-box" role="radiogroup" aria-label="Undertone selection">
          {undertoneOptions.map((item, index) => {
            // Keep the exact same 4+ logic as before
            const isActive = pricestepData?.colorUndertone === item.value;
            // const isActive = (pricestepData?.colorUndertone === "4+" ? "4+" : pricestepData?.colorUndertone) === item.value;
            
            return (
              <div className="idu-image-container" key={index}>
                <Button
                  ref={el => undertoneRefs.current[index] = el}
                  className={`idu-image ${isActive ? "active-image" : ""}`}
                  data-field={`undertone-${index}`}
                  onClick={() => selectUndertone(index)}
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onFocus={() => {setFocusedIndex(index)
                    setHoverIndex(index)
                  }}
                 
                  tabIndex={focusedIndex === index ? 0 : -1}
                  role="radio"
                  aria-checked={isActive}
                  aria-label={`Undertone ${item.value}`}
                >
                  <img
                    src={`${clientPath}/pi-idu-images/${pricestepData?.color?.toLowerCase()}/${pricestepData?.color?.toLowerCase()}${
                      pricestepData?.saturation === "Fancy Deep" ? "-deep" : ""
                    }-undertone-${item?.image}.png`}
                    alt=""
                    aria-hidden="true"
                    style={{
                      opacity: getOpacityValue(pricestepData?.saturation),
                    }}
                  />
                </Button>
                <p
                  style={{
                    background: item?.bg,
                    color: pricestepData?.color === "Yellow" ? "#5c6da4" : "#ffff",
                  }}
                  onClick={() => selectUndertone(index)}
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                  tabIndex="-1"
                >
                  {item?.value}
                </p>
              </div>
            );
          })}
        </div>
        <div className="idu-text-box">
          <p>
            {undertoneOptions[hoverIndex ?? (pricestepData?.colorUndertone - 1)]?.text}
          </p>
        </div>
      </div>
      <div className="idu-bottom-section">
        {Number(pricestepData?.colorUndertone === "4+" ? 5 : pricestepData?.colorUndertone) > 2 &&
          Number(pricestepData?.colorUndertone === "4+" ? 5 : pricestepData?.colorUndertone) < 6 && (
            <h6>
              <img
                src={`${clientPath}/icons/idu_info.svg`}
                height="21px"
                width="21px"
                alt="alert"
              />
              Overvaluing Visual Features Leads to Wrong Pricing!
            </h6>
          )}
      </div>
    </div>
  );
};

export default UndertoneComponent;